import { apiRequest, submitMultipartFormData, /*extractErrorMessage*/ } from "services/APIRequest";

/**
 * Services for checkout-related API operations
 */

/**
 * Removes an item from the cart
 * @param {Object} params - Parameters for the operation
 * @param {string} params.cartToken - Cart token
 * @param {Array} params.currentArticles - Current articles array
 * @param {Array} params.tickets - Ticket information
 * @param {Object} params.formData - Form data
 * @param {string} params.otk - Organization token
 * @returns {Promise<Object>} API response
 */
export const removeItemFromCart = async ({ cartToken, currentArticles, tickets, formData, otk }) => {
	const payload = {
		item: { article: currentArticles },
		invitations: tickets.map(ticket => ({
			article_id: ticket.baseItemId,
			is_for_orderer: ticket.isForOrderer ? 1 : 0,
			email: ticket.isForOrderer ? formData.email : ticket.invitee.email,
			first_name: ticket.isForOrderer ? formData.name : ticket.invitee.firstName,
			last_name: ticket.isForOrderer ? formData.lastName : ticket.invitee.lastName,
			privacy: 1,
			preferences: ticket.isForOrderer ? ticket.preferences : [],
			extra_info: { ticket_number: ticket.ticketNumber }
		}))
	};

	return apiRequest(`checkout/${cartToken}`, {
		method: "PUT",
		data: payload,
		otk,
		raw: true,
		debug: true
	});
};

/**
 * Updates the quantity of an item in the cart
 * @param {Object} params - Parameters for the operation
 * @param {string} params.cartToken - Cart token
 * @param {Array} params.currentArticles - Current articles array
 * @param {Array} params.tickets - Ticket information
 * @param {Object} params.formData - Form data
 * @param {string} params.otk - Organization token
 * @returns {Promise<Object>} API response
 */
export const updateItemQuantity = async ({ cartToken, currentArticles, tickets, formData, otk }) => {
	const payload = {
		item: { article: currentArticles },
		invitations: tickets.map(ticket => ({
			article_id: ticket.baseItemId,
			is_for_orderer: ticket.isForOrderer ? 1 : 0,
			email: ticket.isForOrderer ? formData.email : ticket.invitee.email,
			first_name: ticket.isForOrderer ? formData.name : ticket.invitee.firstName,
			last_name: ticket.isForOrderer ? formData.lastName : ticket.invitee.lastName,
			privacy: 1,
			preferences: ticket.preferences || [],
			extra_info: { ticket_number: ticket.ticketNumber }
		}))
	};

	return apiRequest(`checkout/${cartToken}`, {
		method: "PUT",
		data: payload,
		otk,
		raw: true,
		debug: true
	});
};

/**
 * Applies a promo code to the cart
 * @param {Object} params - Parameters for the operation
 * @param {string} params.cartToken - Cart token
 * @param {string} params.code - Promo code
 * @param {string} params.otk - Organization token
 * @returns {Promise<Object>} API response
 */
export const applyPromoCodeToCart = async ({ cartToken, code, otk }) => {
	const response = await apiRequest("coupons/check", {
		method: "POST",
		data: { code, cart_token: cartToken },
		otk,
		raw: true,
		debug: true
	});

	if (response.httpStatus === 200 && response.status?.message) {
		return apiRequest(`checkout/${cartToken}`, {
			otk,
			raw: true,
			debug: true
		});
	}

	return response;
};

/**
 * Removes a promo code from the cart
 * @param {Object} params - Parameters for the operation
 * @param {string} params.cartToken - Cart token
 * @param {string} params.code - Promo code
 * @param {string} params.otk - Organization token
 * @returns {Promise<Object>} API response
 */
export const removePromoCodeFromCart = async ({ cartToken, code, otk }) => {
	await apiRequest("coupons/remove", {
		method: "DELETE",
		data: { code, cart_token: cartToken },
		otk,
		raw: true,
		debug: true
	});

	return apiRequest(`checkout/${cartToken}`, {
		otk,
		raw: true,
		debug: true
	});
};

/**
 * Submits the checkout form
 * @param {Object} params - Parameters for the operation
 * @param {string} params.cartToken - Cart token
 * @param {Object} params.payload - Form data payload
 * @param {string} params.otk - Organization token
 * @returns {Promise<Object>} API response
 */
export const submitCheckoutForm = async ({ cartToken, payload, otk }) => {
	return submitMultipartFormData(`checkout/${cartToken}`, payload, {
		debug: true,
		otk
	});
};

/**
 * Finalizes an order with ticket generation
 * @param {Object} params - Parameters for the operation
 * @param {string} params.orderId - Order ID
 * @param {Object} params.orderData - Order data
 * @param {string} params.otk - Organization token
 * @returns {Promise<Object>} API response
 */
export const finalizeOrder = async ({ orderId, orderData, otk }) => {
	return apiRequest(`users/orders/${orderId}?generateTickets=true`, {
		method: "PUT",
		data: orderData,
		otk,
		raw: true,
		debug: true
	});
};
